import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  Chip,
  Modal,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  Breadcrumbs,
} from "@mui/material";
import { privateReq } from "../../config/axiosConfig";
import toast from "react-hot-toast";
import Header from "../../components/Header";
import ScrollToTop from "../../components/ScrollToTop.js";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PeopleIcon from "@mui/icons-material/People";
import AddIcon from "@mui/icons-material/Add";
import { Pencil, Trash } from "lucide-react";
import { Link } from "react-router-dom";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 600,
  maxHeight: "90vh",
  overflow: "auto",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: 24,
  p: 4,
};

const SetRoleAdmin = ({
  closeLoginModal,
  setCloseLoginModal,
  setOpenPopUp,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    role: "",
    status: "",
    department: "",
  });
  const [enquiries, setEnquiries] = useState([]);
  const [department, setDepartment] = useState([]);
  const headerRef = useRef();
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    fetchEnquiries();
    onGetDepartment();
    setHeaderHeight(headerRef.current.offsetHeight);
  }, []);

  const onGetDepartment = async () => {
    const response = await privateReq.get("/department/Active");
    setDepartment(response.data.data);
  };

  const fetchEnquiries = async () => {
    try {
      const response = await privateReq.get("/get-set-roles");
      setEnquiries(response.data.data);
    } catch (err) {
      toast.error("Failed to fetch role management");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      role: "",
      status: "",
      department: "",
      _id: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;
      if (formData._id) {
        response = await privateReq.put(
          `/update-set-role/${formData._id}`,
          formData
        );
      } else {
        response = await privateReq.post("/create-set-role", formData);
      }
      if (response.data) {
        toast.success("Role added successfully");
        resetForm();
        fetchEnquiries();
        setOpenModal(false);
      }
    } catch (err) {
      console.error("Error adding role:", err);
      toast.error(err.response?.data?.message || "Failed to add role");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleSwitchChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      status: event.target.checked,
    }));
  };

  const handleEdit = (id) => {
    setFormData({
      _id: id,
      name: enquiries.find((enquiry) => enquiry._id === id).name,
      department: enquiries.find((enquiry) => enquiry._id === id).department
        ._id,
      status: enquiries.find((enquiry) => enquiry._id === id).status,
    });
    setOpenModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this role?")) {
      privateReq
        .delete(`/delete-set-role/${id}`)
        .then(() => {
          toast.success("Role deleted successfully");
          fetchEnquiries();
        })
        .catch((err) => {
          toast.error(err.response?.data?.message || "Failed to delete role");
        });
    } else {
      return;
    }
  };
  return (
    <>
      <ScrollToTop />
      <Header
        closeLoginModal={closeLoginModal}
        setCloseLoginModal={setCloseLoginModal}
        setOpenPopUp={setOpenPopUp}
        innerRef={headerRef}
      />
      <Box
        sx={{
          p: 4,
          maxWidth: "100%",
          mt: `${headerHeight}px`,
          background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)",
          minHeight: "100vh",
        }}
      >

<div style={{marginLeft:"15px", marginTop:"1px", marginBottom:"10px"}}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link underline="hover" color="inherit" to="/admin">
              Dashboard
            </Link>

            <Link underline="hover" color="inherit" to="/admin-settings">
              Admin Setting
            </Link>

            <Typography sx={{ color: "text.primary" }}>
              Role Management
            </Typography>
          </Breadcrumbs>
          </div>

        <Paper
          elevation={3}
          sx={{
            width: "100%",
            overflow: "hidden",
            borderRadius: "16px",
            background: "white",
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            mb: 4,
          }}
        >
          <Box
            sx={{
              p: 1,
              borderBottom: "1px solid #eee",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "3px 10px",
              }}
            >
              <PeopleIcon sx={{ fontSize: 16, color: "#1976d2", mr: 1 }} />
              <Typography variant="p" fontWeight="600" color="#2c3e50">
                Role List
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenModal(true)}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                background: "linear-gradient(45deg, #1976d2 30%, #2196f3 90%)",
                boxShadow: "0 3px 15px rgba(33,150,243,0.3)",
                "&:hover": {
                  background:
                    "linear-gradient(45deg, #1565c0 30%, #1976d2 90%)",
                },
              }}
            >
              Add New Role
            </Button>
          </Box>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                <TableCell
                    sx={{
                      fontWeight: "600",
                      backgroundColor: "#f8fafc",
                      padding: "1px 20px",
                      width: "140px",
                    }}
                  >
                    ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "600",
                      backgroundColor: "#f8fafc",
                      padding: "1px 20px",
                      width: "140px",
                    }}
                  >
                    Role Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "600",
                      backgroundColor: "#f8fafc",
                      padding: "1px 20px",
                      width: "300px",
                    }}
                  >
                    Department
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "600",
                      backgroundColor: "#f8fafc",
                      padding: "1px 20px",
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "600",
                      backgroundColor: "#f8fafc",
                      padding: "1px 20px",
                    }}
                  >
                    Created At
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "600",
                      backgroundColor: "#f8fafc",
                      padding: "1px 20px",
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {enquiries.map((enquiry, index) => (
                  <TableRow
                    key={enquiry._id}
                    hover
                    sx={{
                      "&:hover": {
                        backgroundColor: "#f8fafc",
                      },
                    }}
                  >
                    <TableCell style={{ padding: "1px 20px" }}>
                      <Typography variant="caption" color="textSecondary">
                        {index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell style={{ padding: "1px 20px" }}>
                      <Typography variant="caption" color="textSecondary">
                        {enquiry?.name}
                      </Typography>
                    </TableCell>
                    <TableCell style={{ padding: "1px 20px" }}>
                      <Typography variant="caption" color="textSecondary">
                        {enquiry?.department?.name}
                      </Typography>
                    </TableCell>
                    <TableCell style={{ padding: "1px 20px" }}>
                      <Typography variant="caption" color="textSecondary">
                        {enquiry?.status ? "Active" : "Inactive"}
                      </Typography>
                    </TableCell>
                    <TableCell style={{ padding: "1px 20px" }}>
                      <Typography variant="caption" color="textSecondary">
                        {formatDate(enquiry.created)}
                      </Typography>
                    </TableCell>
                    <TableCell
                      style={{
                        padding: "1px 20px",
                        display: "flex",
                        gap: 5,
                      }}
                    >
                      <Typography
                        onClick={() => handleEdit(enquiry._id)}
                        variant="caption"
                        color="textSecondary"
                      >
                        <Pencil size={"18px"} color="gray" />
                      </Typography>
                      <Typography
                        onClick={() => handleDelete(enquiry._id)}
                        variant="caption"
                        color="textSecondary"
                      >
                        <Trash size={"18px"} color="#990000" />
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Modal
          open={openModal}
          onClose={() => {
            setOpenModal(false);
            resetForm();
          }}
          aria-labelledby="add-enquiry-modal"
          aria-describedby="modal-to-add-new-enquiry"
        >
          <Paper sx={modalStyle}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 0 }}>
              <PersonAddIcon sx={{ fontSize: 16, color: "#1976d2", mr: 1 }} />
              <Typography variant="p" fontWeight="600" color="#2c3e50">
                Add New Role
              </Typography>
            </Box>
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Role Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                margin="normal"
                required
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                  },
                }}
              />

              <select
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                required
                style={{
                  width: "100%",
                  height: "40px",
                  marginTop: "10px",
                  padding: "8px",
                  borderRadius: "12px",
                  border: "1px solid #ccc",
                }}
              >
                <option value="">Select Department</option>
                {department.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.name}
                  </option>
                ))}
              </select>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mt: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.status}
                      onChange={handleSwitchChange}
                      name="status"
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {formData.status ? "Active" : "Inactive"}
                    </Typography>
                  }
                />
              </Box>
              <Button
                type="submit"
                variant="contained"
                disabled={!formData.name || !formData.department}
                fullWidth
                sx={{
                  mt: 1,
                  height: "48px",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontSize: "16px",
                  fontWeight: "600",
                  background: `linear-gradient(45deg, ${
                    !formData.name || !formData.department
                      ? "#1976d2"
                      : "#2196f3"
                  } 30%, ${formData.status ? "#2196f3" : "#1976d2"} 90%)`,
                  boxShadow: "0 3px 15px rgba(33,150,243,0.3)",
                  "&:hover": {
                    background: `linear-gradient(45deg, ${
                      !formData.name || !formData.department
                        ? "#1976d2"
                        : "#1565c0"
                    } 30%, ${
                      !formData.name || !formData.department
                        ? "#1976d2"
                        : "#1565c0"
                    } 90%)`,
                  },
                }}
              >
                Add Role
              </Button>
            </form>
          </Paper>
        </Modal>
      </Box>
    </>
  );
};

export default SetRoleAdmin;
